import NoteAddIcon from "@mui/icons-material/NoteAdd";
import SearchIcon from "@mui/icons-material/Search";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { FilePreview } from "components/FilePreview";
import { StyledContainer } from "components/StyledContainer";
import StyledTextField from "components/StyledTextField";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppSelector } from "store";
import { selectCustomerFiles } from "store/slices/files.store";
import { toFileType } from "./fileHelpers";

// Read-only customer files for the job's customer. The request is prefetched by JobDetails
// (so the data is already in the store when this tab is opened) and this tab only appears
// when the "Can view Customer files" engineer setting is enabled.
export const CustomerFilesTab: FC = () => {
  const intl = useIntl();
  const customerFiles = useAppSelector(selectCustomerFiles);
  const [filter, setFilter] = useState("");

  const files = useMemo(() => customerFiles.map(toFileType), [customerFiles]);

  const filteredFiles = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return files;
    return files.filter((f) => f?.name?.toLowerCase().includes(query));
  }, [files, filter]);

  return (
    <StyledContainer data-testid="CustomerFilesTab">
      {files.length > 0 && (
        <Grid sx={{ mb: 1 }}>
          <StyledTextField
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            variant="outlined"
            placeholder={intl.formatMessage({ id: "file.search" })}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            data-testid="CustomerFilesTab-Search"
          />
        </Grid>
      )}

      {!filteredFiles.length && (
        <Grid container direction="column" alignItems="center">
          <Grid item>
            <NoteAddIcon color="secondary" fontSize="large" />
          </Grid>
          <Grid item>
            <Typography color="secondary">
              <FormattedMessage id="file.noFilesSelected" />
            </Typography>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={1}>
        {filteredFiles.map(
          (f, index) => f && <FilePreview key={index} file={f} downloadOnCardClick readonly />
        )}
      </Grid>
    </StyledContainer>
  );
};
